import { fmtMonthDayYear } from '@concord/domain';
import { Button, Icon, Notice, PageHead, Panel, PanelHead } from '@concord/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import type { Me } from '../../lib/session';
import { ConsoleLayout } from '../console/ConsoleLayout';
import { InviteAccount } from './InviteAccount';

interface Row {
  id: string;
  publicId: number;
  roleLabel: string;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  registrationState: 'invited' | 'registered';
  lastLoginAt: string | null;
  planName: string | null;
  isTest: boolean;
  enabled: boolean;
}

const ROLE: Record<string, string> = { patient: 'Patients', navigator: 'Navigators', expert: 'Physicians' };

/**
 * DIR-01 to DIR-04: search across name, email, phone, ID and case reference, with advanced
 * criteria as removable chips. Test accounts and disabled accounts are excluded by default.
 */
export function DirectoryPage({ me }: { me: Me }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const role = params.get('role') ?? '';
  const planId = params.get('planId') ?? '';
  const registration = params.get('registration') ?? '';
  const includeTest = params.get('includeTest') === 'true';
  const enabledOnly = params.get('enabledOnly') !== 'false';
  const [text, setText] = useState(q);
  const [inviting, setInviting] = useState(false);
  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: () => api<{ items: { id: string; name: string; customer: string }[] }>('/staff/directory/plans'),
  });
  const qs = new URLSearchParams({
    q,
    includeTest: String(includeTest),
    enabledOnly: String(enabledOnly),
    ...(role ? { role } : {}),
    ...(planId ? { planId } : {}),
    ...(registration ? { registration } : {}),
  });
  const res = useQuery({
    queryKey: ['directory', qs.toString()],
    queryFn: () => api<{ rows: Row[]; total: number; caseRef: string | null }>(`/staff/directory?${qs}`),
  });
  const set = (k: string, v: string | null) => {
    const p = new URLSearchParams(params);
    if (v === null || v === '') p.delete(k);
    else p.set(k, v);
    setParams(p, { replace: true });
  };
  const planName = plans.data?.items.find((p) => p.id === planId);
  const chips = [
    !includeTest && {
      key: 'includeTest',
      label: 'Exclude test users',
      clear: () => set('includeTest', 'true'),
    },
    enabledOnly && { key: 'enabledOnly', label: 'Enabled only', clear: () => set('enabledOnly', 'false') },
    role && { key: 'role', label: ROLE[role] ?? role, clear: () => set('role', null) },
    planId &&
      planName && {
        key: 'plan',
        label: `${planName.customer} › ${planName.name}`,
        clear: () => set('planId', null),
      },
    registration && {
      key: 'reg',
      label: registration === 'invited' ? 'Not yet registered' : 'Registered',
      clear: () => set('registration', null),
    },
  ].filter(Boolean) as { key: string; label: string; clear: () => void }[];

  return (
    <ConsoleLayout me={me} current="directory">
      <PageHead
        title="Directory"
        when="Patients, physicians and staff. Search by name, email, phone, ID or case reference."
        actions={
          <Button onClick={() => setInviting(true)}>
            <Icon.Plus />
            Invite an account
          </Button>
        }
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          set('q', text.trim() || null);
        }}
        style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', alignItems: 'center' }}
      >
        <input
          className="input"
          style={{ maxWidth: 360 }}
          aria-label="Search the directory"
          placeholder="Name, email, phone, ID or CX reference"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Button type="submit" variant="sec">
          Search
        </Button>
        <select
          className="select"
          style={{ width: 'auto' }}
          aria-label="Role"
          value={role}
          onChange={(e) => set('role', e.target.value)}
        >
          <option value="">Any role</option>
          <option value="patient">Patients</option>
          <option value="expert">Physicians</option>
          <option value="navigator">Navigators</option>
        </select>
        <select
          className="select"
          style={{ width: 'auto' }}
          aria-label="Plan"
          value={planId}
          onChange={(e) => set('planId', e.target.value)}
        >
          <option value="">Any plan</option>
          {plans.data?.items.map((p) => (
            <option key={p.id} value={p.id}>
              {p.customer} › {p.name}
            </option>
          ))}
        </select>
        <select
          className="select"
          style={{ width: 'auto' }}
          aria-label="Registration"
          value={registration}
          onChange={(e) => set('registration', e.target.value)}
        >
          <option value="">Any registration</option>
          <option value="registered">Registered</option>
          <option value="invited">Not yet registered</option>
        </select>
      </form>
      {res.isError && <Notice tone="bad">{errorMessage(res.error)}</Notice>}
      <Panel aria-label="Accounts">
        <PanelHead title="Accounts" sub={res.data ? `${res.data.total} found` : ''} />
        <div
          style={{
            display: 'flex',
            gap: '.45rem',
            flexWrap: 'wrap',
            alignItems: 'center',
            padding: '.65rem 1.1rem',
            borderBottom: '1px solid var(--rule)',
            background: 'var(--sunk)',
            fontSize: '.78rem',
            color: 'var(--ink-3)',
          }}
        >
          Criteria
          {chips.map((c) => (
            <span
              key={c.key}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '.4rem',
                background: 'var(--accent-soft)',
                color: 'var(--accent)',
                borderRadius: 99,
                padding: '.22rem .3rem .22rem .65rem',
                fontSize: '.76rem',
                fontWeight: 550,
              }}
            >
              {c.label}
              <button
                type="button"
                aria-label={`Remove ${c.label}`}
                onClick={c.clear}
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  background: 'var(--surface)',
                  fontSize: '.7rem',
                }}
              >
                ×
              </button>
            </span>
          ))}
          {chips.length > 0 && (
            <button
              type="button"
              className="link"
              style={{ marginLeft: 'auto' }}
              onClick={() =>
                setParams(
                  q
                    ? { q, includeTest: 'true', enabledOnly: 'false' }
                    : { includeTest: 'true', enabledOnly: 'false' },
                )
              }
            >
              Clear all
            </button>
          )}
        </div>
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Role</th>
                <th>Name</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Country</th>
                <th>ID</th>
                <th>Last login</th>
              </tr>
            </thead>
            <tbody>
              {res.data?.rows.map((r) => (
                <tr
                  key={r.id}
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/console/directory/${r.id}`)}
                  onKeyDown={(e) => e.key === 'Enter' && navigate(`/console/directory/${r.id}`)}
                >
                  <td>{r.roleLabel}</td>
                  <td className="pt">
                    {r.firstName} {r.lastName}
                    {r.registrationState === 'invited' && <div className="sub2">Invited, not registered</div>}
                    {r.isTest && <div className="sub2">Test account</div>}
                  </td>
                  <td>{r.email}</td>
                  <td>{r.planName ?? '—'}</td>
                  <td>{r.country}</td>
                  <td className="mono">{r.publicId}</td>
                  <td>{r.lastLoginAt ? fmtMonthDayYear(new Date(r.lastLoginAt), me.timezone) : 'Never'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <InviteAccount open={inviting} onOpenChange={setInviting} />
    </ConsoleLayout>
  );
}
